import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable image uploader. Uploads file to /api/upload/image and returns the
 * absolute URL via onChange. Shows a preview if `value` is set.
 *
 * Props:
 *  - value: string  (current absolute or relative image URL)
 *  - onChange: (url: string) => void
 *  - testIdPrefix: string  (e.g. "event-image", "library-image", "profile-image")
 *  - label?: string
 *  - aspect?: "square" | "video" | "auto"  (preview aspect ratio)
 */
export default function ImageUploader({ value, onChange, testIdPrefix = 'image', label = 'Image', aspect = 'video' }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const aspectClass =
        aspect === 'square' ? 'aspect-square'
        : aspect === 'auto' ? ''
        : 'aspect-video';

    const previewSrc = value
        ? (value.startsWith('http') ? value : `${API}${value}`)
        : null;

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error('File too large. Max 8 MB.');
            return;
        }
        const fd = new FormData();
        fd.append('file', file);
        setUploading(true);
        try {
            const res = await axios.post(`${API}/api/upload/image`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Store the RELATIVE path (e.g. /api/uploads/<id>) so the same DB
            // row works across preview, production and future environments.
            // The <img> helper (`resolveImageUrl`) prepends REACT_APP_BACKEND_URL
            // at render time, which is already environment-correct.
            onChange(res.data.url || '');
            toast.success('Image uploaded');
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Upload failed';
            toast.error(detail);
        } finally {
            setUploading(false);
            // reset so selecting the same file again retriggers
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const clear = () => onChange('');

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
            <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68]">
                {label}
            </label>

            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleFile}
                data-testid={`${testIdPrefix}-file-input`}
            />

            {previewSrc ? (
                <div className={`relative w-full ${aspectClass} bg-[#F2EBE5] border border-[#E5E0D8] overflow-hidden`}>
                    <img
                        src={previewSrc}
                        alt="preview"
                        className="w-full h-full object-cover"
                        data-testid={`${testIdPrefix}-preview`}
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="bg-white/95 hover:bg-white border border-[#E5E0D8] px-3 py-1.5 text-xs font-sans uppercase tracking-widest text-[#1A1A2E] transition"
                            data-testid={`${testIdPrefix}-replace-btn`}
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Replace'}
                        </button>
                        <button
                            type="button"
                            onClick={clear}
                            className="bg-white/95 hover:bg-white border border-[#E5E0D8] p-1.5 text-[#1A1A2E] transition"
                            data-testid={`${testIdPrefix}-remove-btn`}
                            aria-label="Remove image"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className={`w-full ${aspectClass} flex flex-col items-center justify-center gap-3 border border-dashed border-[#5B2D8E]/40 bg-[#F2EBE5] hover:bg-[#EBE2D8] text-[#4A4A68] transition disabled:opacity-50`}
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    {uploading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E]" />
                    ) : (
                        <Upload className="w-8 h-8 text-[#5B2D8E]" />
                    )}
                    <span className="font-sans text-xs uppercase tracking-[0.2em]">
                        {uploading ? 'Uploading…' : 'Click to upload image'}
                    </span>
                    <span className="font-sans text-xs text-[#4A4A68]/70">
                        PNG, JPG, WEBP, GIF · max 8 MB
                    </span>
                </button>
            )}
        </div>
    );
}
